import { useEffect, useMemo, useState } from "react";

import Select from 'react-select';
import DatePicker from 'react-date-picker';
import ButtonLoader from './ButtonLoader';

import useLicenseTypes from "../hooks/useLicenseTypes";
import { toApiDateTime, formatDateToSydneyTimeZone } from "../utils/dateUtils";

const UploadLicenseForm = ({ license, licenseType, disabledLicenseTypes = [], isUploading, onSubmit, onCloseClicked }) => {
  const initialDateIssued = license?.dateIssued
    ? new Date(formatDateToSydneyTimeZone(license.dateIssued, 'yyyy-MM-dd'))
    : null;
  const [dateIssued, setDateIssued] = useState(initialDateIssued);

  const initialExpirationDate = license?.expirationDate
    ? new Date(formatDateToSydneyTimeZone(license.expirationDate, 'yyyy-MM-dd'))
    : null;
  const [expirationDate, setExpirationDate] = useState(initialExpirationDate);

  const { data, isLoading } = useLicenseTypes({ page: 1, perPage: 100 });
  const licenseTypes = data?.licenseTypes;

  const [selectedLicenseType, setSelectedLicenseType] = useState();
  const [file, setFile] = useState(null);

  const licenseTypeOptions = useMemo(() => {
    return licenseTypes
      ?.filter(licenseType => !disabledLicenseTypes.includes(licenseType._id))
      ?.map(licenseType => ({
        value: licenseType._id,
        label: `${licenseType.hrwlCode} - ${licenseType.name}`
      }));
  }, [licenseTypes, disabledLicenseTypes]);

  useEffect(() => {
    if (licenseType && selectedLicenseType?.value !== licenseType._id && licenseTypeOptions) {
      setSelectedLicenseType(licenseTypeOptions.find(l => l.value === licenseType._id));
    }
  }, [licenseType, licenseTypeOptions, selectedLicenseType]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const resolvedType = selectedLicenseType?.value ?? licenseType?._id;

    if (!resolvedType) return;

    const payload = {
      dateIssued: toApiDateTime(dateIssued),
      expirationDate: toApiDateTime(expirationDate),
      type: resolvedType,
      ...(file ? { file } : {})
    };

    onSubmit(payload);
  }

  return (
    <div className="bg-white rounded-lg border border-ase-blue w-full md:w-[600px] overflow-visible">
      <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
        <p>Upload License</p>

        <p
          onClick={onCloseClicked}
          className="font-bold cursor-pointer text-2xl"
        >
          X
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col w-full overflow-visible"
      >
        <div className="p-4 flex flex-col gap-4">
          <label className='flex flex-col gap-1'>
            <span>License Type</span>

            <Select
              value={selectedLicenseType}
              onChange={setSelectedLicenseType}
              options={licenseTypeOptions}
              isDisabled={license || licenseType || isLoading}
              required={true}
            />
          </label>

          <div className="flex items-center justify-center gap-4">
            <label className="flex flex-col gap-1 w-full">
              Date issued

              <DatePicker
                value={dateIssued}
                onChange={setDateIssued}
                required={true}
                clearIcon={null}
                format="d/M/yyyy"
                className="h-[46px]"
                maxDetail="month"
                minDetail="year"
              />
            </label>

            <label className="flex flex-col gap-1 w-full">
              Expiry date

              <DatePicker
                value={expirationDate}
                onChange={setExpirationDate}
                minDate={dateIssued || null}
                required={true}
                clearIcon={null}
                format="d/M/yyyy"
                className="h-[46px]"
                maxDetail="month"
                minDetail="year"
              />
            </label>
          </div>

          <div>
            <label
              className="block mb-2 text-sm font-medium"
              htmlFor="file_input"
            >
              Upload file
            </label>

            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              required={!license}
              id="file_input"
              className="block p-4 w-full text-sm text-white rounded-lg cursor-pointer bg-ase-blue focus:outline-none ase-upload-input"
            />
          </div>

          <button
            type="submit"
            className="ase-button-primary h-[48px] w-full flex items-center justify-center"
          >
            {isUploading ? <ButtonLoader /> : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UploadLicenseForm;
